import { LatLngTuple } from "leaflet";
import { useEffect, useRef, useState } from "react";
import { Marker, TileLayer } from "react-leaflet";
import { getSharedOccurrenceData } from "services/OccurrenceService";
import { useParams } from "react-router-dom";
import { IOccurrence } from "interfaces/Occurrence";
import { markerIcon } from "components/Maps/MarkerIcon";
import Loader from "components/Modals/OccurrenceManagementModal/Loader";
import OccurrenceInfo from "./OccurrenceInfo";
import { socket } from "hooks/useSocket";
import { Coords } from "interfaces/Coords";
import { PolicemanList, TenantList } from "utils/treatSocketLocations";
import { useAuth } from "contexts/authContext";
import { ExpiredTokenError } from "errors/ExpiredTokenError";
import { FinishedOccurrenceError } from "errors/FinishedOccurrenceError";
import { AdaptedLocation } from "adapters/LocationsAdapter";
import * as L from "leaflet";
import LocationsService, {
  GetLocationsParams,
} from "services/LocationsService";
import {
  Content,
  ErrorMessage,
  OccurrenceInfoContainer,
  Overlay,
  StyledMap,
  StyledMapContainer,
  AutoCompleteContainer
} from "./styles";
import { fireToast } from "services/sweetAlertConfig";
import { AutoComplete } from "components/Autocomplete";
import { IReport } from "interfaces/Report";

export type SocketMarkerBody = {
  coords: Coords;
  token: string;
  working: boolean;
  name?: string;
};

export default function SharedOccurrence() {
  const [policemanLocation, setPolicemanLocation] = useState<LatLngTuple>();
  const [occurrenceLocation, setOccurrenceLocation] = useState<LatLngTuple>();
  const [occurrence, setOccurrence] = useState<IOccurrence | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [polyline, setPolyline] = useState<L.Polyline | null>(null);
  const [polylineLocations, setPolylineLocations] = useState<AdaptedLocation>(
    []
  );
  const [selectedReport, setSelectedReport] = useState<IReport | null>(null)

  const { shareToken } = useParams<{ shareToken: string }>();

  const { tenant } = useAuth();

  const mapRef = useRef<L.Map>(null);

  async function fetchPolylineCoords(params: GetLocationsParams) {
    try {
      const polylineCoords = await LocationsService.getLocations(params);
      setPolylineLocations(polylineCoords);
    } catch {
      fireToast(
        "ATENÇÃO! O apoio ainda nao enviou nenhuma coordenada, portanto nao sera possivel visualizar o trajeto.",
        4000,
        "top-right"
      );
    }
  }

  useEffect(() => {
    const detachedPolicemanId = selectedReport?.superficialPoliceman?.id;
    const policemanId = selectedReport?.policeman?.id;

    if (detachedPolicemanId) {
      fireToast(
        "ATENÇÃO! O apoio associado não consta em nossa base de dados, portanto não será possível visualizar o trajeto.",
        4000,
        "top-right"
      );
      return;
    }

    if (occurrence && occurrence.report && policemanId) {
      const params: GetLocationsParams = {
        policemanId,
        reportId: selectedReport?.id,
      };

      fetchPolylineCoords(params);
    }
  }, [occurrence, selectedReport]);

  async function fetchOccurrence() {
    try {
      const response = await getSharedOccurrenceData(shareToken);

      if (response?.item) {
        const occurrenceCoords: LatLngTuple = [
          Number(response.item.lastCoordX),
          Number(response.item.lastCoordY),
        ];

        setOccurrence(response.item);
        setOccurrenceLocation(occurrenceCoords);

        mapRef.current?.flyTo(occurrenceCoords);
      }
    } catch (error: any) {
      if (error instanceof ExpiredTokenError) {
        setErrorMessage(
          "Esse link expirou! Entre em contato com o responsavel para gerar um novo link valido."
        );
      }

      if (error instanceof FinishedOccurrenceError) {
        setErrorMessage("Esta ocorrência ja foi finalizada.");
      }
    }
  }

  useEffect(() => {
    fetchOccurrence();
  }, []);

  function getPolicemanLocation(objList: PolicemanList) {
    const detachedPolicemanId =
      selectedReport?.superficialPoliceman?.id;
    const policemanId = selectedReport?.policeman?.id;

    if (detachedPolicemanId) {
      fireToast(
        "ATENÇÃO! O apoio associado não consta em nossa base de dados, portanto não será possível visualizar o trajeto.",
        4000,
        "top-right"
      );
    }

    if (policemanId && objList[policemanId]?.coords?.latitude) {
      setPolicemanLocation([
        objList[policemanId].coords.latitude,
        objList[policemanId].coords.longitude,
      ]);
    }
  }

  function handleSocketLocationChange(locations: TenantList | PolicemanList) {
    const tenantId = tenant?.id;
    if (!tenantId) return;

    if (tenantId in locations) {
      getPolicemanLocation(locations[tenantId] as PolicemanList);
    } else {
      getPolicemanLocation(locations as PolicemanList);
    }
  }

  useEffect(() => {
    socket.on("onLocationChange", handleSocketLocationChange);

    if (tenant?.id) {
      socket.emit("listLocations", tenant.id);
      socket.on("listLocations", handleSocketLocationChange);
    }

    return () => {
      socket.off("onLocationChange");
      socket.off("listLocations");
    };
  }, [occurrence, tenant]);

  return (
    <Overlay>
      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
      {occurrence && (
        <Content>
          <OccurrenceInfoContainer data-cy="shared-occurrence-info-container">
            <AutoCompleteContainer>
              <AutoComplete
                label="Selecione o PR para ver informações relacionadas"
                placeholder="Selecione o PR para ver informações relacionadas"
                isMulti={false}
                options={occurrence.report}
                getOptionLabel={report => report?.superficialPoliceman?.name ?? report.policeman.user.name}
                onChange={report => setSelectedReport(report as IReport)}
              />
            </AutoCompleteContainer>

            {occurrence && (
              <OccurrenceInfo
                selectedReport={selectedReport}
                occurrence={occurrence}
                isTrackButtonDisabled={polylineLocations.length === 0}
                onTrackButtonClick={() => {
                  setPolyline((prev) => {
                    if (!prev) {
                      const mapPolyline = L.polyline(polylineLocations, {
                        color: "#191919",
                      }).addTo(mapRef.current!);

                      return mapPolyline;
                    } else {
                      mapRef.current!.removeLayer(prev);
                      return null;
                    }
                  });
                }}
              />
            )}
            {!occurrence && <Loader color="white" size={90} />}
          </OccurrenceInfoContainer>

          <StyledMapContainer data-cy="shared-occurrence-page-map">
            {!occurrenceLocation && <Loader color="white" size={90} />}

            {occurrenceLocation && (
              <StyledMap center={occurrenceLocation} zoom={10} ref={mapRef}>
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />

                {!!policemanLocation && (
                  <Marker
                    icon={markerIcon({
                      color: "black",
                      type: "policemanOnDuty",
                    })}
                    position={{
                      lat: policemanLocation[0],
                      lng: policemanLocation[1],
                    }}
                    eventHandlers={{
                      click: () => { },
                    }}
                  />
                )}

                {!!occurrenceLocation && (
                  <Marker
                    icon={markerIcon({
                      color: "white",
                      type: "occPatrimony",
                    })}
                    position={{
                      lat: occurrenceLocation[0],
                      lng: occurrenceLocation[1],
                    }}
                    eventHandlers={{
                      click: () => { },
                    }}
                  />
                )}
              </StyledMap>
            )}
          </StyledMapContainer>
        </Content>
      )}
    </Overlay>
  );
}
